import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "../../../../context/ThemeContext";
import { Card, Image, Button, Form, Modal, Spinner } from "react-bootstrap";
import { ThumbsUp, ImageIcon, X, Edit2, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FacebookSelector, FacebookCounter } from "@charkour/react-reactions";
import { useComments } from "../../hooks";
import usePlan from "../../../../hooks/usePlan";
import {
  createCommentFormData,
  linkifyText,
  parseTextWithLinksAndMentions,
  generateAvatarUrl,
} from "../../utils/communityHelpers";
import { ReactComponent as Reply } from "../../../../assets/images/reply.svg";
import { getUser } from "../../../../utils/auth";
import { getPreviewLink } from "../../../../redux/slices/editPostSlice";
import { fetchCommunityUsage } from "../../../../redux/slices/communityAllowanceSlice";
import MentionInput from "../mention-input";
import "./CommentsSection.scss";
import wowImg from "../../../../assets/images/Community_emojis/wow.png";
import hahaImg from "../../../../assets/images/Community_emojis/haha.png";
import loveImg from "../../../../assets/images/Community_emojis/Heart.png";
import angryImg from "../../../../assets/images/Community_emojis/angry.png";
import sadImg from "../../../../assets/images/Community_emojis/sad.png";
import UserProfileModal from "../user-profile-modal/UserProfileModal";
import RestrictionModal from "../../../restriction-modals/RestrictionModal";
/**
 * CommentsSection Component
 * Displays and manages comments and nested replies for a post
 * Integrates with API via useComments hook
 * Uses optimistic updates for reactions (Facebook-style)
 * Supports full emoji reactions (like, love, haha, wow, sad, angry)
 */
const ExpandableText = ({ children, isReply }) => {
  return (
    <p className={isReply ? "reply-text" : "comment-text"}>
      {children}
    </p>
  );
};

const CommentsSection = ({ postId, onCommentCountUpdate, scrollToCommentId = null }) => {
  // ============= HOOKS =============
  const { resolvedTheme } = useTheme();
  // HEIC/HEIF files can't be previewed by the browser, so we show this
  // placeholder instead — themed so it doesn't render as a bright box in
  // dark mode (it's a plain data URI, so it can't react to CSS/theme changes
  // once set — rebuilt per-theme instead).
  const heicPlaceholder =
    resolvedTheme === "dark"
      ? "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%231a2830'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%239aa7ac'%3EHEIC Image%3C/text%3E%3C/svg%3E"
      : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%23666'%3EHEIC Image%3C/text%3E%3C/svg%3E";
  const {
    comments,
    loading,
    error,
    fetchComments,
    addComment,
    editComment,
    deleteComment,
    toggleLikeComment,
    addOrUpdateCommentReaction,
    removeCommentReaction,
    totalComments,
  } = useComments(postId);

  // Get current user from Redux
  const currentUser = useSelector((state) => state.auth?.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ============= STATE =============
  // User Profile Modal state
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);

  // Subscription restriction state
  const [showPaywallSheet, setShowPaywallSheet] = useState(false);

  // Restriction shown when a free user tries to write a comment or reply
  const [showWriteRestriction, setShowWriteRestriction] = useState(false);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isFree } = usePlan();

  // Free users get a small weekly comment allowance (2/week); paid = unlimited.
  const commentAllowance = useSelector((state) => state.communityAllowance.comment);
  // remaining comments to show in the UI (null when unlimited or not yet loaded)
  const commentsRemaining =
    isFree && commentAllowance && commentAllowance.limit >= 0
      ? commentAllowance.remaining
      : null;

  // Load the weekly allowance on mount (this component can render outside the
  // Community page — e.g. in the post-detail modal or the dashboard).
  useEffect(() => {
    dispatch(fetchCommunityUsage());
  }, [dispatch]);

  /**
   * Gate comment/reply writing once a free user has used up their weekly
   * allowance. While they still have comments left they can type and post
   * freely. Returns true when blocked (and shows the upgrade modal).
   */
  const blockWriteIfFree = () => {
    if (commentsRemaining !== null && commentsRemaining <= 0) {
      setShowWriteRestriction(true);
      return true;
    }
    return false;
  };

  const handleUserProfileClick = (e, comment) => {
    e.stopPropagation();

    // Don't open for own comments
    if (comment._metadata?.userId === currentUser?.id) return;

    // Don't open for Anonymous users
    if (comment.author === "Anonymous" || comment._metadata?.isAnonymous === 1) return;

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (isFree) {
      setShowPaywallSheet(true);
      return;
    }

    // Set user profile data
    setSelectedUserProfile({
      id: comment._metadata?.userId,
      name: comment.author,
      image: comment.authorImage,
      state: comment._metadata?.userState
    });

    setShowUserProfileModal(true);
  };

  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [collapsedComments, setCollapsedComments] = useState({});

  // Link preview state for comments
  const [commentLinkPreview, setCommentLinkPreview] = useState(null);
  const [isCommentPreviewLoading, setIsCommentPreviewLoading] = useState(false);
  const commentPreviewTimeoutRef = useRef(null);

  // Link preview state for replies
  const [replyLinkPreview, setReplyLinkPreview] = useState(null);
  const [isReplyPreviewLoading, setIsReplyPreviewLoading] = useState(false);
  const replyPreviewTimeoutRef = useRef(null);

  // Edit state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const editImageInputRef = useRef(null);

  // Link preview state for edit mode
  const [editLinkPreview, setEditLinkPreview] = useState(null);
  const [isEditPreviewLoading, setIsEditPreviewLoading] = useState(false);
  const editPreviewTimeoutRef = useRef(null);

  // Image upload state for comments
  const [commentImage, setCommentImage] = useState(null);
  const [commentImagePreview, setCommentImagePreview] = useState(null);
  const [replyImage, setReplyImage] = useState(null);
  const [replyImagePreview, setReplyImagePreview] = useState(null);
  const commentImageInputRef = useRef(null);
  const replyImageInputRef = useRef(null);

  // Reaction picker state (cosmetic only - all map to simple like)
  const [commentPickerVisible, setCommentPickerVisible] = useState({});
  const [replyPickerVisible, setReplyPickerVisible] = useState({});
  const pickerHideTimers = useRef({});

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  // Reddit-style thread view state for mobile
  const [focusedThreadId, setFocusedThreadId] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);

  // Progressive expansion state - tracks which replies are expanded
  const [expandedReplies, setExpandedReplies] = useState({});

  // Focused reply thread - for nested reply expansion
  const [focusedReplyId, setFocusedReplyId] = useState(null);

  // Track whether we've already scrolled to the target comment
  const scrolledRef = useRef(false);

  // ============= HELPER FUNCTIONS =============
  /**
   * Extract URL from text
   */
  const extractUrl = (text) => {
    if (!text) return null;

    const urlRegex = /((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*))/gi;

    const match = text.match(urlRegex);
    if (!match || !match[0]) return null;

    let url = match[0].trim();

    // Add https:// if protocol is missing
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    try {
      const parsedUrl = new URL(url);

      // Ensure trailing slash for root domains
      if (!parsedUrl.pathname || parsedUrl.pathname === "") {
        parsedUrl.pathname = "/";
      }

      return parsedUrl.toString();
    } catch {
      return null;
    }
  };

  /**
   * Check if preview is valid
   */
  const isValidPreview = (preview) => {
    if (!preview) return false;

    const { title, description, image, domain } = preview;

    return (
      Boolean(title?.trim()) ||
      Boolean(description?.trim()) ||
      Boolean(image?.trim()) ||
      Boolean(domain?.trim())
    );
  };

  // ============= EFFECTS =============
  /**
   * Fetch link preview for new comment
   */
  useEffect(() => {
    const url = extractUrl(newComment);

    if (!url) {
      setCommentLinkPreview(null);
      setIsCommentPreviewLoading(false);
      return;
    }

    if (commentPreviewTimeoutRef.current) {
      clearTimeout(commentPreviewTimeoutRef.current);
    }

    commentPreviewTimeoutRef.current = setTimeout(async () => {
      try {
        setIsCommentPreviewLoading(true);

        const res = await dispatch(getPreviewLink({ url })).unwrap();
        setCommentLinkPreview(res?.editPost || null);
      } catch {
        setCommentLinkPreview(null);
      } finally {
        setIsCommentPreviewLoading(false);
      }
    }, 2000);

    return () => clearTimeout(commentPreviewTimeoutRef.current);
  }, [newComment, dispatch]);

  /**
   * Fetch link preview for reply
   */
  useEffect(() => {
    const url = extractUrl(replyText);

    if (!url) {
      setReplyLinkPreview(null);
      setIsReplyPreviewLoading(false);
      return;
    }

    if (replyPreviewTimeoutRef.current) {
      clearTimeout(replyPreviewTimeoutRef.current);
    }

    replyPreviewTimeoutRef.current = setTimeout(async () => {
      try {
        setIsReplyPreviewLoading(true);

        const res = await dispatch(getPreviewLink({ url })).unwrap();
        setReplyLinkPreview(res?.editPost || null);
      } catch {
        setReplyLinkPreview(null);
      } finally {
        setIsReplyPreviewLoading(false);
      }
    }, 2000);

    return () => clearTimeout(replyPreviewTimeoutRef.current);
  }, [replyText, dispatch]);
  /**
   * Fetch link preview for edit mode
   */
  useEffect(() => {
    // Only fetch if we're in edit mode
    if (!editingCommentId) {
      setEditLinkPreview(null);
      setIsEditPreviewLoading(false);
      return;
    }

    const url = extractUrl(editText);

    if (!url) {
      setEditLinkPreview(null);
      setIsEditPreviewLoading(false);
      return;
    }

    if (editPreviewTimeoutRef.current) {
      clearTimeout(editPreviewTimeoutRef.current);
    }

    editPreviewTimeoutRef.current = setTimeout(async () => {
      try {
        setIsEditPreviewLoading(true);

        const res = await dispatch(getPreviewLink({ url })).unwrap();
        setEditLinkPreview(res?.editPost || null);
      } catch {
        setEditLinkPreview(null);
      } finally {
        setIsEditPreviewLoading(false);
      }
    }, 2000);

    return () => clearTimeout(editPreviewTimeoutRef.current);
  }, [editText, editingCommentId, dispatch]);
  /**
   * Load comments when component mounts or postId changes
   * CRITICAL: Only run on mount and when postId changes, NOT when fetchComments changes!
   */
  useEffect(() => {
    if (postId) {
      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]); // Only postId, NOT fetchComments!

  /**
   * Detect mobile view for Reddit-style threading
   */
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkMobileView();
    window.addEventListener("resize", checkMobileView);

    return () => window.removeEventListener("resize", checkMobileView);
  }, []);

  /**
   * Notify parent when comment count changes
   * Uses ref to track previous count and prevent unnecessary updates
   */
  const previousCountRef = useRef(totalComments);

  useEffect(() => {
    // Only notify parent if the count actually changed
    if (
      onCommentCountUpdate &&
      totalComments !== undefined &&
      previousCountRef.current !== totalComments
    ) {
      previousCountRef.current = totalComments;
      onCommentCountUpdate(totalComments);
    }
  }, [totalComments, onCommentCountUpdate]);

  /**
   * Scroll to a specific comment when navigating from a notification.
   * If the comment is a buried reply, expands its thread first.
   */
  useEffect(() => {
    if (!scrollToCommentId || loading || comments.length === 0 || scrolledRef.current) return;
    scrolledRef.current = true;

    const scrollAndHighlight = (el) => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "background-color 0.4s ease";
      el.style.backgroundColor = "var(--mmp-accent-bg)";
      setTimeout(() => {
        el.style.transition = "background-color 1.5s ease";
        el.style.backgroundColor = "";
      }, 2000);
    };

    const searchInReplies = (replies, targetId) => {
      for (const r of replies) {
        if (r.id === targetId) return true;
        if (r.replies?.length && searchInReplies(r.replies, targetId)) return true;
      }
      return false;
    };

    const tryScroll = () => {
      const el = document.getElementById(`comment-${scrollToCommentId}`);
      if (el) {
        scrollAndHighlight(el);
        return;
      }
      // Element not in DOM yet — find its top-level parent and expand that thread
      for (const c of comments) {
        if (c.replies?.length && searchInReplies(c.replies, scrollToCommentId)) {
          setFocusedThreadId(c.id);
          setTimeout(() => {
            const targetEl = document.getElementById(`comment-${scrollToCommentId}`);
            if (targetEl) scrollAndHighlight(targetEl);
          }, 350);
          return;
        }
      }
    };

    setTimeout(tryScroll, 400);
  }, [scrollToCommentId, loading, comments]);

  // ============= HANDLERS =============
  /**
   * Handle comment image selection
   */
  const handleCommentImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - allow images and HEIC/HEIF files
    const isImage = file.type.startsWith("image/");
    const isHeic = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
    
    if (!isImage && !isHeic) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setCommentImage(file);
    
    // For HEIC/HEIF files, show a placeholder since browsers can't preview them
    if (isHeic) {
      // Create a placeholder data URL
      setCommentImagePreview(heicPlaceholder);
    } else {
      setCommentImagePreview(URL.createObjectURL(file));
    }
  };

  /**
   * Handle reply image selection
   */
  const handleReplyImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - allow images and HEIC/HEIF files
    const isImage = file.type.startsWith("image/");
    const isHeic = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
    
    if (!isImage && !isHeic) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setReplyImage(file);
    
    // For HEIC/HEIF files, show a placeholder since browsers can't preview them
    if (isHeic) {
      // Create a placeholder data URL with SVG
      setReplyImagePreview(heicPlaceholder);
    } else {
      setReplyImagePreview(URL.createObjectURL(file));
    }
  };

  /**
   * Remove comment image
   */
  const removeCommentImage = () => {
    if (commentImagePreview) {
      URL.revokeObjectURL(commentImagePreview);
    }
    setCommentImage(null);
    setCommentImagePreview(null);
    if (commentImageInputRef.current) {
      commentImageInputRef.current.value = "";
    }
  };

  /**
   * Remove comment link preview
   */
  const removeCommentLinkPreview = () => {
    setCommentLinkPreview(null);
  };

  /**
   * Remove reply image
   */
  const removeReplyImage = () => {
    if (replyImagePreview) {
      URL.revokeObjectURL(replyImagePreview);
    }
    setReplyImage(null);
    setReplyImagePreview(null);
    if (replyImageInputRef.current) {
      replyImageInputRef.current.value = "";
    }
  };

  /**
   * Remove reply link preview
   */
  const removeReplyLinkPreview = () => {
    setReplyLinkPreview(null);
  };

  /**
   * Show/hide reaction picker with delay
   */
  const showPicker = (key) => {
    const timers = pickerHideTimers.current;
    if (timers[key]) clearTimeout(timers[key]);

    if (key.includes("-r-")) {
      setReplyPickerVisible((prev) => ({ ...prev, [key]: true }));
    } else {
      setCommentPickerVisible((prev) => ({ ...prev, [key]: true }));
    }
  };

  const hidePicker = (key) => {
    const timers = pickerHideTimers.current;
    timers[key] = setTimeout(() => {
      if (key.includes("-r-")) {
        setReplyPickerVisible((prev) => ({ ...prev, [key]: false }));
      } else {
        setCommentPickerVisible((prev) => ({ ...prev, [key]: false }));
      }
    }, 250);
  };

  /**
   * Add new comment with optimistic UI
   * Clears input immediately, restores on error
   */
  const handleAddComment = async () => {
    const trimmedComment = newComment?.trim();
    if (!trimmedComment && !commentImage) return;
    // Backstop: weekly allowance may have run out since the user started typing.
    if (blockWriteIfFree()) return;

    // Save for rollback
    const savedComment = newComment;
    const savedImage = commentImage;
    const savedPreview = commentImagePreview;
    const savedLinkPreview = commentLinkPreview;

    try {
      const formData = createCommentFormData({
        postId,
        content: trimmedComment || "",
        image: commentImage,
        link_preview: commentLinkPreview || {},
      });

      // Clear input immediately (optimistic)
      setNewComment("");
      removeCommentImage();
      setCommentLinkPreview(null);

      const result = await addComment(formData);

      // Restore on failure
      if (!result?.success) {
        setNewComment(savedComment);
        setCommentImage(savedImage);
        setCommentImagePreview(savedPreview);
        setCommentLinkPreview(savedLinkPreview);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setNewComment(savedComment);
      setCommentImage(savedImage);
      setCommentImagePreview(savedPreview);
      setCommentLinkPreview(savedLinkPreview);
    }
  };

  /**
   * Add reply to comment with optimistic UI
   * Clears and closes input immediately, restores on error
   */
  const handleAddReply = async (parentCommentId) => {
    const trimmedReply = replyText?.trim();
    if (!trimmedReply && !replyImage) return;
    // Backstop: weekly allowance may have run out since the user started typing.
    if (blockWriteIfFree()) return;

    // Save for rollback
    const savedReply = replyText;
    const savedImage = replyImage;
    const savedPreview = replyImagePreview;
    const savedLinkPreview = replyLinkPreview;

    try {
      const formData = createCommentFormData({
        postId,
        content: trimmedReply || "",
        parentId: parentCommentId,
        image: replyImage,
        link_preview: replyLinkPreview || {},
      });

      // Clear and close immediately (optimistic)
      setReplyText("");
      setReplyingTo(null);
      removeReplyImage();
      setReplyLinkPreview(null);

      const result = await addComment(formData, parentCommentId);

      // Restore on failure
      if (!result?.success) {
        setReplyText(savedReply);
        setReplyingTo(parentCommentId);
        setReplyImage(savedImage);
        setReplyImagePreview(savedPreview);
        setReplyLinkPreview(savedLinkPreview);
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      setReplyText(savedReply);
      setReplyingTo(parentCommentId);
      setReplyImage(savedImage);
      setReplyImagePreview(savedPreview);
      setReplyLinkPreview(savedLinkPreview);
    }
  };

  /**
   * Toggle comment like with optimistic update
   * Handles both comments and nested replies
   */
  const handleToggleLike = async (commentId) => {
    try {
      await toggleLikeComment(commentId);
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  /**
   * Handle reaction pick - uses actual emoji reaction API
   */
  const handleReactionPick = async (commentId, reaction) => {
    // Hide picker
    const key = `c-${commentId}`;
    setCommentPickerVisible((prev) => ({ ...prev, [key]: false }));

    try {
      await addOrUpdateCommentReaction(commentId, reaction);
    } catch (error) {
      console.error("Error adding reaction:", error);
    }
  };

  /**
   * Handle reply reaction pick - uses actual emoji reaction API
   */
  const handleReplyReactionPick = async (commentId, replyId, reaction) => {
    const key = `c-${commentId}-r-${replyId}`;
    setReplyPickerVisible((prev) => ({ ...prev, [key]: false }));

    try {
      await addOrUpdateCommentReaction(replyId, reaction);
    } catch (error) {
      console.error("Error adding reaction to reply:", error);
    }
  };

  /**
   * Toggle collapse/expand comment
   */
  const toggleCollapseComment = (commentId) => {
    setCollapsedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  /**
   * Start editing a comment
   */
  const handleStartEdit = (comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.content || "");
    setEditImage(null);
    setEditImagePreview(null);
    // Set existing link preview if available
    setEditLinkPreview(comment.link_preview || null);
  };

  /**
   * Cancel editing
   */
  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText("");
    if (editImagePreview) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImage(null);
    setEditImagePreview(null);
    setEditLinkPreview(null);
  };

  /**
   * Handle edit image selection
   */
  const handleEditImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - allow images and HEIC/HEIF files
    const isImage = file.type.startsWith("image/");
    const isHeic = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
    
    if (!isImage && !isHeic) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setEditImage(file);
    
    // For HEIC/HEIF files, show a placeholder since browsers can't preview them
    if (isHeic) {
      // Create a placeholder data URL with SVG
      setEditImagePreview(heicPlaceholder);
    } else {
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  /**
   * Remove edit image
   */
  const removeEditImage = () => {
    if (editImagePreview) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImage(null);
    setEditImagePreview(null);
    if (editImageInputRef.current) {
      editImageInputRef.current.value = "";
    }
  };

  /**
   * Remove edit link preview
   */
  const removeEditLinkPreview = () => {
    setEditLinkPreview(null);
  };

  /**
   * Save edited comment
   */
  const handleSaveEdit = async () => {
    const trimmedText = editText?.trim();
    if (!trimmedText && !editImage) {
      toast.error("Comment cannot be empty");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("id", editingCommentId);
      formData.append("content", trimmedText || "");
      if (editImage) {
        formData.append("image_path", editImage);
      }
      // Add link preview data if available
      if (editLinkPreview) {
        formData.append("link_preview", JSON.stringify(editLinkPreview));
      }

      const result = await editComment(formData);
      if (result?.success) {
        handleCancelEdit();
      }
    } catch (error) {
      console.error("Error saving edit:", error);
    }
  };

  /**
   * Open delete confirmation modal
   */
  const handleDeleteComment = (commentId) => {
    setCommentToDelete(commentId);
    setShowDeleteModal(true);
  };

  /**
   * Confirm delete action
   */
  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await deleteComment(commentToDelete);
      setShowDeleteModal(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  /**
   * Cancel delete action
   */
  const cancelDeleteComment = () => {
    setShowDeleteModal(false);
    setCommentToDelete(null);
  };

  /**
   * Handle "Show more" click - focuses on a specific thread (mobile only)
   */
  const handleShowMoreReplies = (commentId) => {
    setFocusedThreadId(commentId);
    // Scroll to top of comments section
    document
      .querySelector(".comments-section-card")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /**
   * Handle "Show more" click for nested replies - focuses on a specific reply thread
   */
  const handleShowMoreNestedReplies = (replyId) => {
    setFocusedReplyId(replyId);
    // Don't scroll - let the user stay where they are
    // The focused view will appear without scrolling
  };

  /**
   * Handle "See full discussion" click - returns to all comments view
   */
  const handleSeeFullDiscussion = () => {
    setFocusedThreadId(null);
    setFocusedReplyId(null);
  };

  /**
   * Toggle expansion of nested replies (progressive disclosure)
   */
  const toggleReplyExpansion = (commentId) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  // ============= HELPER FUNCTIONS =============
  /**
   * Get current user's avatar image
   */
  const getUserAvatar = () => {
    if (currentUser?.profile_photo_path) {
      return `${process.env.REACT_APP_API_BASE_URL}/${currentUser.profile_photo_path}`;
    }
    const userName = currentUser?.full_name || currentUser?.username || "User";
    return generateAvatarUrl(userName);
  };

  /**
   * Find comment by ID (recursive for nested replies)
   */
  const findCommentById = (commentsList, commentId) => {
    for (const comment of commentsList) {
      if (comment.id === commentId) return comment;
      if (comment.replies && comment.replies.length > 0) {
        const found = findCommentById(comment.replies, commentId);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Find reply by ID (recursive)
   */
  const findReplyById = (commentsList, replyId) => {
    return findCommentById(commentsList, replyId);
  };

  /**
   * Flatten nested replies into a single-level list (Reddit-style)
   * Each reply shows at the same level with "Replying to" label
   */
  const flattenReplies = (comment, depth = 0) => {
    const result = [];

    // Add the comment itself
    result.push({ ...comment, depth });

    // Add all replies at the same level (flattened)
    if (comment.replies && comment.replies.length > 0) {
      comment.replies.forEach((reply) => {
        const flattened = flattenReplies(reply, depth + 1);
        result.push(...flattened);
      });
    }

    return result;
  };

  /**
   * Get the parent comment/reply author name for "Replying to" label
   */
  const getParentAuthorName = (parentCommentId) => {
    if (!parentCommentId) return null;
    const parentComment = findCommentById(comments, parentCommentId);
    return parentComment?.author || null;
  };

  /**
   * Render a single comment with all its features
   * Recursively renders nested replies
   */
  const renderComment = (
    comment,
    isReply = false,
    parentCommentId = null,
    depth = 0,
    isFocusedMode = false,
  ) => {
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isEditing = editingCommentId === comment.id;
    const isCollapsed = collapsedComments[comment.id];

    // Get current user ID from localStorage
    const currentUser = getUser();
    const currentUserId = currentUser?.id;

    // Compare user IDs instead of author names
    const isOwnComment = comment._metadata?.userId === currentUserId;

    if (!comment?.author || !comment?._metadata?.userId) return null;

    // Skip rendering if user info is missing
    if (!comment?.author && !comment?._metadata?.userId) {
      console.warn("Skipping comment with undefined user:", comment);
      return null;
    }
    const reactionIcons = {
      like: (
        <ThumbsUp
          height={22}
          fill={comment.userReaction ? "currentColor" : "none"}
        />
      ),
      love: <img src={loveImg} alt="love" width={24} height={22} />,
      haha: <img src={hahaImg} alt="haha" width={24} height={22} />,
      wow: <img src={wowImg} alt="wow" width={24} height={22} />,
      sad: <img src={sadImg} alt="sad" width={24} height={22} />,
      angry: <img src={angryImg} alt="angry" width={24} height={22} />,
    };

    // Count total nested replies recursively
    const countNestedReplies = (replies) => {
      if (!replies || replies.length === 0) return 0;
      let count = replies.length;
      replies.forEach((reply) => {
        if (reply.replies && reply.replies.length > 0) {
          count += countNestedReplies(reply.replies);
        }
      });
      return count;
    };

    const totalNestedReplies = hasReplies
      ? countNestedReplies(comment.replies)
      : 0;

    return (
      <div
        key={comment.id}
        id={`comment-${comment.id}`}
        className={isReply ? "reply-item" : "comment-thread"}
        style={{
          opacity: comment._optimistic ? 0.6 : 1,
          transition: "opacity 0.3s ease",
        }}
      >
        <div className={isReply ? "reply-main" : "comment-main"}>
          {/* Collapse/Expand Button */}
          <button
            onClick={() => toggleCollapseComment(comment.id)}
            className="collapse-toggle-btn"
            title={isCollapsed ? "Expand comment" : "Collapse comment"}
            aria-label={isCollapsed ? "Expand comment" : "Collapse comment"}
          >
            {isCollapsed ? "+" : "−"}
          </button>

          <Image
            src={`${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${comment.authorImage}`}
            alt={comment.author || "User"}
            className={isReply ? "reply-avatar" : "comment-avatar"}
            roundedCircle
            style={{ cursor: "pointer", objectFit: "cover" }}
            onClick={(e) => handleUserProfileClick(e, comment)}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                comment.author || "User",
              )}&background=0195D3&color=fff&size=42`;
            }}
          />
          <div className={isReply ? "reply-content" : "comment-content"}>
            {/* Show collapsed state */}
            {isCollapsed ? (
              <div className="collapsed-comment">
                <span className="collapsed-author">
                  {comment.author === "undefined undefined"
                    ? "Deleted user"
                    : comment.author}
                </span>
                <span className="collapsed-info">
                  {hasReplies &&
                    ` [${comment.replies.length} ${comment.replies.length === 1 ? "reply" : "replies"
                    }]`}
                </span>
              </div>
            ) : (
              <>
                <div className={isReply ? "reply-bubble" : "comment-bubble"}>
                  <div className={isReply ? "reply-header" : "comment-header"}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start", // Changed from center to flex-start
                        width: "100%",
                        gap: "8px", // Add gap between content and buttons
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}> {/* Allow this to shrink */}
                        <div
                          className={
                            isReply ? "reply-author" : "comment-author"
                          }
                          style={{ cursor: "pointer" }}
                          onClick={(e) => handleUserProfileClick(e, comment)}
                        >
                          {comment.author === "undefined undefined"
                            ? "Deleted user"
                            : comment.author}
                        </div>
                        <div
                          className={isReply ? "reply-time" : "comment-time"}
                        >
                          {comment._optimistic
                            ? "Posting..."
                            : comment.time || "Just now"}
                        </div>
                      </div>
                      {isOwnComment && !comment._optimistic && (
                        <div style={{
                          display: "flex",
                          gap: "2px",
                          flexShrink: 0, // Prevent buttons from shrinking
                          alignSelf: "flex-start" // Align to top
                        }}>
                          {/* Only show edit button if comment is still editable (within 5 minutes) */}
                          {comment.isEditable && (
                            <Button
                              variant="link"
                              onClick={() => handleStartEdit(comment)}
                              style={{
                                padding: "4px",
                                minWidth: "auto",
                                color: "var(--mmp-accent)",
                                opacity: 0.7,
                              }}
                              title="Edit comment"
                            >
                              <Edit2 size={18} />
                            </Button>
                          )}
                          <Button
                            variant="link"
                            onClick={() => handleDeleteComment(comment.id)}
                            className="m-0"
                            style={{
                              padding: "4px",
                              minWidth: "auto",
                              color: "var(--mmp-danger)",
                              opacity: 0.7,
                            }}
                            title="Delete comment"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  {isEditing ? (
                    <div style={{ marginTop: "8px" }}>
                      {editImagePreview && (
                        <div
                          style={{ position: "relative", marginBottom: "8px" }}
                        >
                          <img
                            src={editImagePreview}
                            alt="Preview"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "150px",
                              borderRadius: "8px",
                              objectFit: "cover",
                              border: "1px solid var(--mmp-border)",
                            }}
                          />
                          <button
                            onClick={removeEditImage}
                            style={{
                              position: "absolute",
                              top: "4px",
                              right: "4px",
                              background: "rgba(0, 0, 0, 0.6)",
                              border: "none",
                              borderRadius: "50%",
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              padding: 0,
                            }}
                            aria-label="Remove image"
                          >
                            <X size={16} color="white" />
                          </button>
                        </div>
                      )}

                      {/* Edit Link Preview Loading */}
                      {isEditPreviewLoading &&
                        !isValidPreview(editLinkPreview) && (
                          <div className="link-preview loading mb-2">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">
                              Fetching link preview...
                            </span>
                          </div>
                        )}

                      {/* Edit Link Preview Card */}
                      {!isEditPreviewLoading &&
                        isValidPreview(editLinkPreview) && (
                          <div className="link-preview-card mb-2 p-2 border rounded position-relative">
                            <button
                              type="button"
                              className="btn-close position-absolute top-0 end-0 m-2"
                              onClick={removeEditLinkPreview}
                            />

                            <div className="d-flex gap-2">
                              {editLinkPreview.image && (
                                <Image
                                  src={editLinkPreview.image}
                                  width={80}
                                  height={80}
                                  className="rounded"
                                  style={{ objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}

                              <div className="flex-grow-1">
                                {editLinkPreview.title && (
                                  <h6 className="mb-1 text-truncate">
                                    {editLinkPreview.title}
                                  </h6>
                                )}

                                {editLinkPreview.description && (
                                  <p
                                    className="mb-0 text-muted small"
                                    style={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {editLinkPreview.description}
                                  </p>
                                )}

                                {editLinkPreview.domain && (
                                  <small className="text-primary">
                                    {editLinkPreview.domain}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px",
                          position: "relative",
                        }}
                      >
                        <Form.Control
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              !e.shiftKey &&
                              editText?.trim() &&
                              !isEditPreviewLoading
                            ) {
                              e.preventDefault();
                              handleSaveEdit();
                            } else if (e.key === "Escape") {
                              handleCancelEdit();
                            }
                          }}
                          // , paddingRight: '22px'
                          style={{
                            flex: 1,
                            border: "1px solid var(--mmp-border)",
                            marginBottom: "10px",
                          }}
                          autoFocus
                        />
                        <input
                          ref={editImageInputRef}
                          type="file"
                          accept="image/*,.heic,.heif"
                          onChange={handleEditImageSelect}
                          style={{ display: "none" }}
                        />
                        <Button
                          variant="link"
                          onClick={() => editImageInputRef.current?.click()}
                          style={{
                            padding: "0px",
                            minWidth: "auto",
                            color: "var(--mmp-text-secondary)",
                            position: "absolute",
                            left: "auto",
                            right: "10px",
                            top: "4px",
                          }}
                          title="Add image"
                        >
                          <ImageIcon size={18} />
                        </Button>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <Button
                          variant="primary"
                          onClick={handleSaveEdit}
                          disabled={!editText?.trim() || isEditPreviewLoading}
                          // size="sm"
                          style={{ padding: "12px 18px" }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={handleCancelEdit}
                          // size="sm"
                          style={{ padding: "12px 18px", marginLeft: "0" }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Comment Text - only show if there's content */}
                      {comment.content && (
                        <ExpandableText isReply={isReply}>
                          {parseTextWithLinksAndMentions(
                            comment.content || "",
                          ).map((part, index) => {
                            if (part.type === "link") {
                              return (
                                <a
                                  key={index}
                                  href={part.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "var(--mmp-accent)",
                                    textDecoration: "underline",
                                    wordBreak: "break-all",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {part.content}
                                </a>
                              );
                            } else if (part.type === "mention") {
                              return (
                                <span
                                  key={index}
                                  style={{
                                    color: "var(--mmp-accent)",
                                    fontWeight: 500,
                                  }}
                                >
                                  {part.content}
                                </span>
                              );
                            } else if (part.type === "email") {
                              return (
                                <span
                                  key={index}
                                  style={{
                                    color: "#00bcd4", // Cyan color similar to the image
                                    fontWeight: 500,
                                  }}
                                >
                                  {part.content}
                                </span>
                              );
                            }
                            return <span key={index}>{part.content}</span>;
                          })}
                        </ExpandableText>
                      )}

                      {/* Comment Image */}
                      {comment.image && (
                        <div
                          style={{ marginTop: comment.content ? "8px" : "0" }}
                        >
                          <img
                            src={comment.image}
                            alt="Comment attachment"
                            style={{
                              maxWidth: "300px",
                              maxHeight: "300px",
                              borderRadius: "8px",
                              objectFit: "cover",
                              border: "1px solid var(--mmp-border)",
                              cursor: "pointer",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = "none";
                              console.error(
                                "Failed to load comment image:",
                                comment.image,
                              );
                            }}
                            onClick={(e) => {
                              // Open image in new tab on click
                              window.open(comment.image, "_blank");
                            }}
                          />
                        </div>
                      )}

                      {/* Comment Link Preview */}
                      {comment.link_preview &&
                        isValidPreview(comment.link_preview) && (
                          <div
                            className="link-preview-card mt-2 p-2 border rounded"
                            style={{
                              backgroundColor: "var(--mmp-hover-bg)",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              // Extract URL from comment content or use domain
                              const url = extractUrl(comment.content);
                              if (url) {
                                window.open(
                                  url,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }
                            }}
                          >
                            <div className="d-flex gap-2">
                              {comment.link_preview.image && (
                                <Image
                                  src={comment.link_preview.image}
                                  width={80}
                                  height={80}
                                  className="rounded"
                                  style={{ objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}

                              <div className="flex-grow-1">
                                {comment.link_preview.title && (
                                  <h6 className="mb-1 text-truncate">
                                    {comment.link_preview.title}
                                  </h6>
                                )}

                                {comment.link_preview.description && (
                                  <p
                                    className="mb-0 text-muted small"
                                    style={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {comment.link_preview.description}
                                  </p>
                                )}

                                {comment.link_preview.domain && (
                                  <small className="text-primary">
                                    {comment.link_preview.domain}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </div>

                {/* Reaction Summary - hide when editing */}
                {/* {!isEditing && (
              <div className="d-flex align-items-center">
                <div className="reaction-container">
                  <FacebookCounter
                    counters={(() => {
                      const order = ['like', 'love', 'haha', 'wow', 'sad', 'angry'];
                      const counts = comment.reactions || {};
                      const list = [];

                      // HYBRID APPROACH: Calculate how many legacy likes exist (from mobile)
                      const currentReactionTotal = Object.values(counts).reduce((sum, count) => sum + count, 0);
                      const legacyLikesCount = Math.max(0, comment.likes - currentReactionTotal);

                      // Add legacy likes as 'like' reactions (they show as thumbs up)
                      for (let i = 0; i < legacyLikesCount; i++) {
                        list.push({ emoji: 'like', by: `legacy-like-${i}` });
                      }

                      // Add web reactions
                      order.forEach((type) => {
                        const n = Math.max(0, counts[type] || 0);
                        for (let i = 0; i < n; i++) {
                          if (
                            comment.userReaction === type &&
                            !list.some((e) => e.by === 'You' && e.emoji === type)
                          ) {
                            list.push({ emoji: type, by: 'You' });
                          } else {
                            list.push({ emoji: type, by: `user-${type}-${i}` });
                          }
                        }
                      });
                      return list;
                    })()}
                    user={'You'}
                    bg={'var(--mmp-surface)'}
                    alwaysShowOthers={true}
                  />
                </div>
              </div>
            )} */}

                {/* Actions - hide when editing */}
                {/* Reaction Summary - HYBRID: Shows both web reactions + legacy likes */}
                {!isEditing && (
                  <div className="d-flex align-items-center">
                    <div className="reaction-container">
                      {(() => {
                        const totalReactions = Object.values(
                          comment.reactions || {},
                        ).reduce((sum, count) => sum + count, 0);
                        const currentReactionTotal = totalReactions;
                        const legacyLikesCount = Math.max(
                          0,
                          comment.likes - currentReactionTotal,
                        );
                        const allReactionsCount =
                          totalReactions + legacyLikesCount;

                        // Don't show anything if there are no reactions at all
                        if (allReactionsCount === 0) {
                          return null;
                        }

                        const hasUserReaction = comment.userReaction;

                        // If only user reacted (count is 1 and user has reacted)
                        if (allReactionsCount === 1 && hasUserReaction) {
                          // Get the emoji icon for the user's reaction
                          const reactionEmojis = {
                            like: (
                              <ThumbsUp
                                height={22}
                                fill={
                                  comment.userReaction ? "currentColor" : "none"
                                }
                              />
                            ),
                            love: (
                              <img
                                src={loveImg}
                                alt="love"
                                width={24}
                                height={22}
                              />
                            ),
                            haha: (
                              <img
                                src={hahaImg}
                                alt="haha"
                                width={24}
                                height={22}
                              />
                            ),
                            wow: (
                              <img
                                src={wowImg}
                                alt="wow"
                                width={24}
                                height={22}
                              />
                            ),
                            sad: (
                              <img
                                src={sadImg}
                                alt="sad"
                                width={24}
                                height={22}
                              />
                            ),
                            angry: (
                              <img
                                src={angryImg}
                                alt="angry"
                                width={24}
                                height={22}
                              />
                            ),
                          };

                          return (
                            <div className="d-flex align-items-center gap-1">
                              <div
                                className={`d-flex align-items-center gap-2 action-btn ${comment.userReaction
                                    ? "text-primary"
                                    : "text-muted"
                                  }`}
                              >
                                {reactionEmojis[hasUserReaction]}
                              </div>
                              <span style={{ fontSize: "14px" }}>You</span>
                            </div>
                          );
                        }

                        // Otherwise show FacebookCounter for multiple reactions
                        return (
                          <FacebookCounter
                            counters={(() => {
                              const order = [
                                "like",
                                "love",
                                "haha",
                                "wow",
                                "sad",
                                "angry",
                              ];
                              const list = [];
                              const counts = comment.reactions || {};

                              // Calculate how many legacy likes exist (from mobile)
                              const currentReactionTotal = Object.values(
                                counts,
                              ).reduce((sum, count) => sum + count, 0);
                              const legacyLikesCount = Math.max(
                                0,
                                comment.likes - currentReactionTotal,
                              );

                              // Add legacy likes as 'like' reactions (they show as thumbs up)
                              for (let i = 0; i < legacyLikesCount; i++) {
                                list.push({
                                  emoji: "like",
                                  by: `legacy-like-${i}`,
                                });
                              }

                              // Add web reactions
                              order.forEach((type) => {
                                const n = Math.max(0, counts[type] || 0);
                                for (let i = 0; i < n; i++) {
                                  if (
                                    comment.userReaction === type &&
                                    !list.some(
                                      (e) => e.by === "You" && e.emoji === type,
                                    )
                                  ) {
                                    list.push({ emoji: type, by: "You" });
                                  } else {
                                    list.push({
                                      emoji: type,
                                      by: `user-${type}-${i}`,
                                    });
                                  }
                                }
                              });
                              return list;
                            })()}
                            user={"You"}
                            bg={"var(--mmp-surface)"}
                            alwaysShowOthers={true}
                            size={24}
                          />
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Actions - hide when editing */}
                {!isEditing && (
                  <div
                    className={`${isReply ? "reply-actions" : "comment-actions"
                      } position-relative`}
                  >
                    {/* Like button with reaction picker */}
                    <div
                      className="d-inline-block position-relative"
                      onMouseEnter={() =>
                        showPicker(
                          isReply
                            ? `c-${parentCommentId}-r-${comment.id}`
                            : `c-${comment.id}`,
                        )
                      }
                      onMouseLeave={() =>
                        hidePicker(
                          isReply
                            ? `c-${parentCommentId}-r-${comment.id}`
                            : `c-${comment.id}`,
                        )
                      }
                    >
                      <Button
                        variant="link"
                        onClick={() => handleToggleLike(comment.id)}
                        className={`action-button ${comment.userReaction ? "liked" : "muted"
                          } p-0`}
                        disabled={loading}
                      >
                        {/* Show reaction icon if user has reacted, otherwise show default thumbs up */}
                        {comment.userReaction ? (
                          reactionIcons[comment.userReaction] || (
                            <ThumbsUp size={24} fill="currentColor" />
                          )
                        ) : (
                          <ThumbsUp size={24} fill="none" />
                        )}
                        {/* Don't show count in the button - it's shown in the summary above */}
                      </Button>

                      {/* Reaction Picker (cosmetic) */}
                      {(isReply
                        ? replyPickerVisible[
                        `c-${parentCommentId}-r-${comment.id}`
                        ]
                        : commentPickerVisible[`c-${comment.id}`]) && (
                          <div
                            className="reaction-picker-popup"
                            style={{
                              position: "absolute",
                              bottom: "100%",
                              left: 0,
                              marginBottom: 8,
                              zIndex: 1000,
                            }}
                            onMouseEnter={() =>
                              showPicker(
                                isReply
                                  ? `c-${parentCommentId}-r-${comment.id}`
                                  : `c-${comment.id}`,
                              )
                            }
                            onMouseLeave={() =>
                              hidePicker(
                                isReply
                                  ? `c-${parentCommentId}-r-${comment.id}`
                                  : `c-${comment.id}`,
                              )
                            }
                          >
                            <FacebookSelector
                              onSelect={(type) =>
                                isReply
                                  ? handleReplyReactionPick(
                                    parentCommentId,
                                    comment.id,
                                    type,
                                  )
                                  : handleReactionPick(comment.id, type)
                              }
                            />
                          </div>
                        )}
                    </div>

                    {/* Reply button */}
                    <Button
                      variant="link"
                      onClick={() => {
                        if (blockWriteIfFree()) return;
                        setReplyingTo(comment.id);
                      }}
                      className="action-button muted p-0"
                      disabled={loading}
                    >
                      <Reply size={24} />
                      <span>Reply</span>
                    </Button>
                  </div>
                )}

                {/* Reply Input */}
                {replyingTo === comment.id && (
                  <div className="reply-input-section">
                    <Image
                      src={
                        comment.authorImage
                          ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${comment.authorImage}`
                          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            currentUser?.full_name || "You",
                          )}&background=0195D3&color=fff&size=42`
                      }
                      alt="You"
                      className="reply-avatar"
                      roundedCircle
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          currentUser?.full_name || "You",
                        )}&background=0195D3&color=fff&size=42`;
                      }}
                    />
                    <div className="reply-input-wrapper">
                      {/* Reply Image Preview */}
                      {replyImagePreview && (
                        <div
                          style={{ position: "relative", marginBottom: "8px" }}
                        >
                          <img
                            src={replyImagePreview}
                            alt="Preview"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "150px",
                              borderRadius: "8px",
                              objectFit: "cover",
                              border: "1px solid var(--mmp-border)",
                            }}
                          />
                          <button
                            onClick={removeReplyImage}
                            style={{
                              position: "absolute",
                              top: "4px",
                              right: "4px",
                              background: "rgba(0, 0, 0, 0.6)",
                              border: "none",
                              borderRadius: "50%",
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              padding: 0,
                            }}
                            aria-label="Remove image"
                          >
                            <X size={16} color="white" />
                          </button>
                        </div>
                      )}

                      {/* Reply Link Preview Loading */}
                      {isReplyPreviewLoading &&
                        !isValidPreview(replyLinkPreview) && (
                          <div className="link-preview loading mb-2">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">
                              Fetching link preview...
                            </span>
                          </div>
                        )}

                      {/* Reply Link Preview Card */}
                      {!isReplyPreviewLoading &&
                        isValidPreview(replyLinkPreview) && (
                          <div className="link-preview-card mt-2 p-2 border rounded position-relative">
                            <button
                              type="button"
                              className="btn-close position-absolute top-0 end-0 m-2"
                              onClick={removeReplyLinkPreview}
                            />

                            <div className="d-flex gap-2">
                              {replyLinkPreview.image && (
                                <Image
                                  src={replyLinkPreview.image}
                                  width={80}
                                  height={80}
                                  className="rounded"
                                  style={{ objectFit: "cover" }}
                                />
                              )}

                              <div className="flex-grow-1">
                                {replyLinkPreview.title && (
                                  <h6 className="mb-1 text-truncate">
                                    {replyLinkPreview.title}
                                  </h6>
                                )}

                                {replyLinkPreview.description && (
                                  <p className="mb-0 text-muted small line-clamp-2">
                                    {replyLinkPreview.description}
                                  </p>
                                )}

                                {replyLinkPreview.domain && (
                                  <small className="text-primary">
                                    {replyLinkPreview.domain}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px",
                          position: "relative",
                        }}
                      >
                        <MentionInput
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyPress={(e) => {
                            if (
                              e.key === "Enter" &&
                              !e.shiftKey &&
                              (replyText?.trim() || replyImage) &&
                              !isReplyPreviewLoading
                            ) {
                              e.preventDefault();
                              handleAddReply(comment.id);
                            }
                          }}
                          placeholder="Write a reply..."
                          autoFocus
                          className="reply-input"
                          style={{
                            flex: 1,
                            border: "1px solid var(--mmp-border)",
                            paddingRight: "40px" // Reserve space for image button
                          }}
                        />

                        {/* Reply Image Upload Button */}
                        <input
                          ref={replyImageInputRef}
                          type="file"
                          accept="image/*,.heic,.heif"
                          onChange={handleReplyImageSelect}
                          style={{ display: "none" }}
                        />
                        <Button
                          variant="link"
                          onClick={() => replyImageInputRef.current?.click()}
                          style={{
                            padding: "6px",
                            minWidth: "auto",
                            color: "var(--mmp-text-secondary)",
                            position: "absolute",
                            left: "auto",
                            right: "10px",
                            top: "3px",
                          }}
                          title="Add image"
                        >
                          <ImageIcon size={18} />
                        </Button>
                      </div>

                      <div className="reply-buttons">
                        <Button
                          variant="primary"
                          onClick={() => handleAddReply(comment.id)}
                          disabled={
                            (!replyText?.trim() && !replyImage) ||
                            isReplyPreviewLoading
                          }
                          className="btn btn-primary"
                          size="sm"
                        >
                          Reply
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText("");
                            removeReplyImage();
                            setReplyLinkPreview(null);
                          }}
                          className="reply-cancel m-0"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Nested Replies */}
                {hasReplies && (
                  <div className="replies-section">
                    {/* Show latest 3 replies by default at levels 0, 1, 2. For deeper levels, show "Show replies" button */}
                    {(() => {
                      const totalReplies = comment.replies.length;
                      const showLatestCount = 3;
                      const hasMoreReplies = totalReplies > showLatestCount;
                      const maxVisibleDepth = 2; // 0=main comment, 1=reply, 2=nested reply

                      // If we're at depth 3 or deeper, don't show individual replies, just show "Show replies" button
                      if (depth >= maxVisibleDepth + 1) {
                        return (
                          <Button
                            variant="link"
                            onClick={() => handleShowMoreNestedReplies(comment.id)}
                            className="show-more-replies-btn show-more-count"
                          >
                            Show replies
                            {/* Show replies ({totalReplies}) */}
                          </Button>
                        );
                      }

                      // For levels 0, 1, 2: show latest 3 replies with normal logic
                      const isThisFocusedComment = (focusedThreadId === comment.id) || (focusedReplyId === comment.id);
                      const repliesToShow = isThisFocusedComment ? comment.replies : comment.replies.slice(-showLatestCount);

                      return (
                        <>
                          {/* Show replies (latest 3 by default, all only if this specific comment is focused) */}
                          <div className="replies-list">
                            {repliesToShow.map((reply) =>
                              renderComment(
                                reply,
                                true,
                                comment.id,
                                depth + 1,
                                isFocusedMode,
                              ),
                            )}
                          </div>

                          {/* Show "Show replies" button if there are more than 3 replies and this comment is not specifically focused */}
                          {!isThisFocusedComment && hasMoreReplies && (
                            <>
                              {/* For top-level comments on mobile */}
                              {isMobileView &&
                                !isReply &&
                                depth === 0 && (
                                  <Button
                                    variant="link"
                                    onClick={() => handleShowMoreReplies(comment.id)}
                                    className="show-more-replies-btn show-more-count"
                                  >
                                    Show replies
                                    {/* Show replies ({totalReplies}) */}
                                  </Button>
                                )}

                              {/* For nested replies and desktop view */}
                              {(!isMobileView || isReply || depth > 0) && (
                                <Button
                                  variant="link"
                                  onClick={() =>
                                    handleShowMoreNestedReplies(comment.id)
                                  }
                                  className="show-more-replies-btn show-more-count"
                                >
                                  Show replies
                                  {/* Show replies ({totalReplies}) */}
                                </Button>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============= RENDER =============
  return (
    <div className="comments-section-card">
      <Card.Body className="pt-0 pb-0 ">
        <h3 className="comments-title">Comments</h3>

        {/* Loading State - removed for optimistic updates */}

        {/* Error State */}
        {error && comments.length === 0 && (
          <div className="alert alert-danger" role="alert">
            <small>{error}</small>
          </div>
        )}

        {/* "See full discussion" button - shown when viewing a focused thread or focused reply */}
        {(focusedThreadId || focusedReplyId) && (
          <div className="full-discussion-header">
            <Button
              variant="link"
              onClick={handleSeeFullDiscussion}
              className="see-full-discussion-btn"
            >
              ← See full discussion
            </Button>
          </div>
        )}

        {/* Comments List */}
        {!loading && comments.length > 0 && (
          <div className="comments-list">
            {focusedReplyId
              ? // Show focused reply thread
              (() => {
                const focusedReply = findCommentById(
                  comments,
                  focusedReplyId,
                );
                if (!focusedReply) return null;

                // Render the focused reply with all its nested replies
                return renderComment(focusedReply, false, null, 0, true);
              })()
              : focusedThreadId
                ? // Mobile: Show only the focused thread with all its nested replies
                (() => {
                  const focusedComment = findCommentById(
                    comments,
                    focusedThreadId,
                  );
                  if (!focusedComment) return null;

                  // Render the focused comment with nested structure
                  return renderComment(focusedComment, false, null, 0, true);
                })()
                : // Normal view: Show all top-level comments
                comments.map((comment) =>
                  renderComment(comment, false, null, 0, false),
                )}
          </div>
        )}

        {/* Empty State */}
        {!loading && comments.length === 0 && !error && (
          <div className="text-center no-comment">
            <p className="text-muted mb-0">
              No comments yet. Be the first to comment!
            </p>
          </div>
        )}

        {/* Add New Comment */}
        <div className="add-comment-section">
          <div className="add-comment-wrapper">
            <Image
              // src={`${process.env.REACT_APP_API_BASE_URL}/${img.image_path}`}
              // src={currentUser.profile_photo_path ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${currentUser.profile_photo_path}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.full_name)}&background=0195D3&color=fff&size=42`}
              src={
                currentUser?.profile_photo_path
                  ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${currentUser.profile_photo_path}`
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    currentUser?.full_name || "User",
                  )}&background=0195D3&color=fff&size=42`
              }
              alt="You"
              className="add-comment-avatar"
              roundedCircle
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  currentUser?.full_name || "You",
                )}&background=0195D3&color=fff&size=42`;
              }}
            />
            <div className="add-comment-input-wrapper">
              {/* Image Preview */}
              {commentImagePreview && (
                <div
                  style={{
                    position: "relative",
                    marginBottom: "8px",
                    width: "max-content",
                  }}
                >
                  <img
                    src={commentImagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                      borderRadius: "8px",
                      objectFit: "cover",
                      border: "1px solid #000",
                    }}
                  />
                  <button
                    onClick={removeCommentImage}
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      background: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      padding: 0,
                    }}
                    aria-label="Remove image"
                  >
                    <X size={14} color="#000" />
                  </button>
                </div>
              )}

              {/* Link Preview Loading */}
              {isCommentPreviewLoading && !isValidPreview(commentLinkPreview) && (
                <div className="link-preview loading mb-2">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Fetching link preview...</span>
                </div>
              )}

              {/* Link Preview Card */}
              {!isCommentPreviewLoading && isValidPreview(commentLinkPreview) && (
                <div className="link-preview-card mb-2 p-2 border rounded position-relative">
                  <button
                    type="button"
                    className="btn-close position-absolute top-0 end-0 m-2"
                    onClick={removeCommentLinkPreview}
                  />

                  <div className="d-flex gap-2">
                    {commentLinkPreview.image && (
                      <Image
                        src={commentLinkPreview.image}
                        width={80}
                        height={80}
                        className="rounded"
                        style={{ objectFit: "cover" }}
                      />
                    )}

                    <div className="flex-grow-1">
                      {commentLinkPreview.title && (
                        <h6 className="mb-1 text-truncate">
                          {commentLinkPreview.title}
                        </h6>
                      )}

                      {commentLinkPreview.description && (
                        <p className="mb-0 text-muted small line-clamp-2">
                          {commentLinkPreview.description}
                        </p>
                      )}

                      {commentLinkPreview.domain && (
                        <small className="text-primary">
                          {commentLinkPreview.domain}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Free-tier weekly comment allowance hint */}
              {commentsRemaining !== null && (
                <div
                  style={{
                    fontSize: "12px",
                    color: commentsRemaining > 0 ? "var(--mmp-text-secondary)" : "var(--mmp-danger)",
                    margin: "0 2px 6px",
                  }}
                >
                  {commentsRemaining > 0 ? (
                    `${commentsRemaining} free ${commentsRemaining === 1 ? "comment" : "comments"} left this week`
                  ) : (
                    <>
                      You're out of free comments this week —{" "}
                      <span
                        onClick={() =>
                          navigate("/profile/subscription", {
                            state: { source: "comment_limit" },
                          })
                        }
                        style={{
                          color: "var(--mmp-accent)",
                          fontWeight: 600,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        upgrade
                      </span>{" "}
                      for unlimited commenting.
                    </>
                  )}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  position: "relative",
                }}
              >
                <MentionInput
                  value={newComment}
                  onChange={(e) => {
                    if (blockWriteIfFree()) return;
                    setNewComment(e.target.value);
                  }}
                  onKeyPress={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      (newComment?.trim() || commentImage) &&
                      !isCommentPreviewLoading
                    ) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                  placeholder="Add a comment..."
                  className="add-comment-input"
                  style={{
                    flex: 1,
                    border: "1px solid var(--mmp-border)",
                    paddingRight: "60px" // Reserve space for buttons
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    left: "auto",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {/* Image Upload Button */}
                  <input
                    ref={commentImageInputRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    onChange={handleCommentImageSelect}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="link"
                    onClick={() => {
                      if (blockWriteIfFree()) return;
                      commentImageInputRef.current?.click();
                    }}
                    style={{
                      padding: "6px",
                      minWidth: "auto",
                      color: "var(--mmp-text-secondary)",
                    }}
                    title="Add image"
                  >
                    <ImageIcon size={20} />
                  </Button>

                  {(newComment?.trim() || commentImage) && (
                    <div className="add-comment-button-wrapper">
                      <Button
                        onClick={handleAddComment}
                        className="add-comment-submit"
                        disabled={isCommentPreviewLoading}
                        // size="sm"
                        style={{
                          padding: "0 !important",
                          background: "transparent !important",
                        }}
                      >
                        {/* Post */}
                        <Send size={18} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card.Body>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={cancelDeleteComment}
        centered
        dialogClassName="custom-modal delete-post"
      >
        <Modal.Body>
          <h3 className="title pt-0">Delete Comment</h3>
          <p className="mb-0">
            Are you sure you want to delete this comment? This action cannot be
            undone.
          </p>
          <div className="buttons text-end">
            <Button onClick={cancelDeleteComment} className="btn btn-secondary">
              Cancel
            </Button>
            <Button onClick={confirmDeleteComment} className="btn btn-danger">
              Delete
            </Button>
          </div>
        </Modal.Body>
        {/* <Modal.Footer>
          <Button variant="secondary" onClick={cancelDeleteComment}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteComment}>
            Delete
          </Button>
        </Modal.Footer> */}
      </Modal>

      <UserProfileModal
        show={showUserProfileModal}
        onHide={() => setShowUserProfileModal(false)}
        userId={selectedUserProfile?.id}
        userName={selectedUserProfile?.name || ""}
        userImage={selectedUserProfile?.image}
        userState={selectedUserProfile?.state || ""}
      />

      <RestrictionModal
        isVisible={showPaywallSheet}
        onClose={() => setShowPaywallSheet(false)}
        eventSource="follow_limit"
        iconType="user-follow"
        heading="Want to follow or chat with this user?"
        subText="Upgrade to connect and build real relationships."
        infoText="Pro users can follow, chat, and connect with anyone in the community."
      />

      <RestrictionModal
        isVisible={showWriteRestriction}
        onClose={() => setShowWriteRestriction(false)}
        eventSource="comment_limit"
        iconType="user-follow"
        heading="You've used your free comments this week"
        subText="Free members can post 2 comments per week. Upgrade to Pro to comment and reply without limits."
        infoText="Pro users can post comments and replies without limits."
      />
    </div>
  );
};

export default CommentsSection;
